import {faSparkles} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Button, Spin} from 'antd';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {Locale} from '../../../../../provider/locale/locale';
import {ConcordanceAiPresetEnum} from '../../../../../service/reivew-analysis/review-analysis-ai-chat/review-analysis-ai-message-type';
import {
    AiSummaryStateEnum,
    useReviewAnalysisAiSummary,
} from '../../../../../service/reivew-analysis/review-analysis-ai-summary/review-analysis-ai-summary';
import {useReviewsAnalysisDefaultDateRange, useReviewsAnalysisFilter} from '../../reviews-analysis-hook';

import * as styles from './ai-summary-block.scss';

type PropsType = {
    companyId?: number;
    aspectIds?: Array<number>;
};

export function AiSummaryBlock({companyId, aspectIds}: PropsType): JSX.Element {
    // These pages are opened outside the filter drawer, so seed a default period —
    // otherwise the summary request would carry empty dates and 400.
    const defaultDateRange = useReviewsAnalysisDefaultDateRange();
    const {singleFilter} = useReviewsAnalysisFilter(true, defaultDateRange);

    const {summary, state, generate, isReady} = useReviewAnalysisAiSummary({
        companyId,
        aspectIds,
        startDate: singleFilter.startDate,
        endDate: singleFilter.endDate,
        // BST-10541: reuse an existing RocketGPT preset for the summary (V1).
        presetKey: ConcordanceAiPresetEnum.SWOT,
    });

    if (state === AiSummaryStateEnum.Insufficient) {
        return (
            <Text secondary>
                <Locale stringKey="REVIEWS_ANALYSIS__AI_SUMMARY__INSUFFICIENT" />
            </Text>
        );
    }

    if (state === AiSummaryStateEnum.QuotaExceeded) {
        return (
            <Text secondary>
                <Locale stringKey="REVIEWS_ANALYSIS__AI_ANALYSIS__ERROR_LIMITS_REACHED" />
            </Text>
        );
    }

    if (state === AiSummaryStateEnum.Error) {
        return (
            <div className={styles.AiSummaryBlock_error}>
                <Text secondary>
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />
                </Text>
                <Button onClick={generate} size="small">
                    <Locale stringKey="REVIEWS_ANALYSIS__AI_SUMMARY__RETRY" />
                </Button>
            </div>
        );
    }

    if (state === AiSummaryStateEnum.Idle) {
        return (
            <Button disabled={!isReady} icon={<FontAwesomeIcon icon={faSparkles} />} onClick={generate}>
                <Locale stringKey="REVIEWS_ANALYSIS__AI_SUMMARY__GENERATE" />
            </Button>
        );
    }

    if (!summary) {
        return <Spin />;
    }

    return <Markdown remarkPlugins={[remarkGfm]}>{summary}</Markdown>;
}
